import React, { memo, useCallback, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { createUseStyles, Checkbox, Text } from '@Sage/hoi-poi-ui';
import { cleanString } from "../../../utils/string";
import styles from "../styles";
const useStyles = createUseStyles(styles, 'ModalOptions');
const fuseOptions = {
  threshold: 0.2,
  keys: ['label'],
  getFn: (obj, path) => {
    const value = Fuse.config.getFn(obj, path);
    return cleanString(value);
  }
};
const OptionGroup = /*#__PURE__*/memo(_ref => {
  let {
    disabled,
    onSelect,
    schema,
    search,
    sectionId = false,
    selected,
    setContentLength
  } = _ref;
  const classes = useStyles();
  const renderOption = useCallback((option, options, index, parentId, isChecked, isDisabled) => {
    return /*#__PURE__*/React.createElement("div", {
      key: `${option.id}-${index}`,
      className: classes.option
    }, /*#__PURE__*/React.createElement(Checkbox, {
      onChange: onSelect({
        id: option.id,
        parentId,
        options,
        sectionId: sectionId
      }),
      checked: isChecked,
      isDisabled: isDisabled,
      color: "actionMinor"
    }), /*#__PURE__*/React.createElement(Text, {
      className: classes.optionText,
      type: "body"
    }, option.label), option.comment && /*#__PURE__*/React.createElement(Text, {
      className: classes.optionComment,
      type: "body"
    }, option.comment));
  }, [classes.option, classes.optionComment, classes.optionText, onSelect, sectionId]);
  const finalOptions = useCallback(_ref2 => {
    let {
      options,
      parentId,
      parentLabel
    } = _ref2;
    if (!options || options.length === 0) return null;
    const finalSearch = search ? cleanString(search.toLowerCase()) : null;
    const allItems = [{
      id: parentId,
      label: cleanString(parentLabel.toLowerCase())
    }, ...options];
    const fuse = new Fuse(allItems, fuseOptions);
    const matchingOptions = finalSearch ? fuse.search(finalSearch).map(result => result.item.id) : [];
    const matchesParent = matchingOptions.includes(parentId);
    const shouldRenderOption = option => {
      if (!finalSearch) return true;
      if (matchingOptions.length === 0) return false;
      if (matchesParent && matchingOptions.length === 1) return true;
      return matchingOptions.includes(option.id);
    };
    const finalOptions = options.reduce((arr, option, index) => {
      var _selected$parentId, _disabled$parentId;
      if (!option.label || !option.id) return arr;
      const isChecked = (selected === null || selected === void 0 ? void 0 : (_selected$parentId = selected[parentId]) === null || _selected$parentId === void 0 ? void 0 : _selected$parentId.options.includes(option.id)) || false;
      const isDisabled = (disabled === null || disabled === void 0 ? void 0 : (_disabled$parentId = disabled[parentId]) === null || _disabled$parentId === void 0 ? void 0 : _disabled$parentId.options.includes(option.id)) || option.forceDisabled || false;
      if (shouldRenderOption(option)) {
        arr.push(renderOption(option, options, index, parentId, isChecked, isDisabled));
      }
      return arr;
    }, []);
    return finalOptions;
  }, [search, selected, disabled, renderOption]);
  const optionGroupContent = useMemo(() => schema.reduce((arr, group, index) => {
    var _group$options, _selected$group$id, _selected$group$id2, _selected$group$id3, _disabled$group$id3;
    if (!group.id || !group.options) return arr;
    const renderOptions = (group === null || group === void 0 ? void 0 : (_group$options = group.options) === null || _group$options === void 0 ? void 0 : _group$options.length) > 0 && finalOptions({
      options: group.options || [],
      parentId: group.id,
      parentLabel: group.label
    }) || [];
    if (renderOptions.length === 0) return arr;
    const enabledOptions = group.options.filter(option => {
      var _disabled$group$id;
      return !(disabled !== null && disabled !== void 0 && (_disabled$group$id = disabled[group.id]) !== null && _disabled$group$id !== void 0 && _disabled$group$id.options.includes(option.id));
    }) || [];
    const enabledCheckedOptions = (selected === null || selected === void 0 ? void 0 : (_selected$group$id = selected[group.id]) === null || _selected$group$id === void 0 ? void 0 : _selected$group$id.options.filter(option => {
      var _disabled$group$id2;
      return !(disabled !== null && disabled !== void 0 && (_disabled$group$id2 = disabled[group.id]) !== null && _disabled$group$id2 !== void 0 && _disabled$group$id2.options.includes(option));
    })) || [];
    const isIndeterminate = !(selected !== null && selected !== void 0 && (_selected$group$id2 = selected[group.id]) !== null && _selected$group$id2 !== void 0 && _selected$group$id2.isSelected) && enabledCheckedOptions.length > 0 && enabledOptions.length !== enabledCheckedOptions.length || false;
    const isChecked = (selected === null || selected === void 0 ? void 0 : (_selected$group$id3 = selected[group.id]) === null || _selected$group$id3 === void 0 ? void 0 : _selected$group$id3.isSelected) || enabledOptions.length === enabledCheckedOptions.length || false;
    const isDisabled = disabled === null || disabled === void 0 ? void 0 : (_disabled$group$id3 = disabled[group.id]) === null || _disabled$group$id3 === void 0 ? void 0 : _disabled$group$id3.isDisabled;
    arr.push(/*#__PURE__*/React.createElement("div", {
      key: `${group.id}-${index}`,
      className: classes.group
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.groupTitle
    }, /*#__PURE__*/React.createElement(Checkbox, {
      onChange: onSelect({
        id: group.id,
        options: group.options,
        sectionId: sectionId
      }),
      checked: isChecked || isIndeterminate,
      indeterminate: isIndeterminate,
      isDisabled: isDisabled,
      color: "actionMinor"
    }), /*#__PURE__*/React.createElement(Text, {
      className: classes.optionText,
      type: "subtitle",
      medium: true
    }, group.label)), /*#__PURE__*/React.createElement("div", {
      className: classes.optionContainer
    }, renderOptions)));
    return arr;
  }, []), [classes.group, classes.groupTitle, classes.optionContainer, classes.optionText, disabled, finalOptions, onSelect, schema, sectionId, selected]);
  useEffect(() => {
    setContentLength && setContentLength(optionGroupContent.length);
  }, [optionGroupContent, setContentLength]);
  return optionGroupContent;
});
export default OptionGroup;